import React from 'react';
import { MSL_TABLE_HEADER_SUBTEXT, SKELETON_LOADER_CONST } from '@constants/commonConstants';
import TableHeader from '@molecules/msl/components/forecast/tableHeader';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

function formatCurrencyWithoutCents(amount) {
  // Convert to number (ensure it handles cases like "0.000", "1200.00")
  const numericValue = parseFloat(amount); // Default to 0 if null or invalid
  // Handle the special case for 0.000 to show "$0"
  if (numericValue === 0) {
    return '$0';
  }
  // Remove the decimals by flooring the value to the nearest dollar
  const dollarsOnly = Math.floor(numericValue); // Truncate decimals
  // Format the number as currency with commas (US-style grouping)
  const formattedValue = dollarsOnly.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Prepend the dollar sign
  return `$${formattedValue}`;
}
function currencyCellRenderer(params, fieldName) {
  if (params.value === SKELETON_LOADER_CONST.LOADING) {
    return <Skeleton width={150} />;
  }
  // If the row is a group row, display the formatted amount for the group
  if (params.node.group) {
    const allLeafChildren = params?.node?.allLeafChildren || [];
    const totalAmount = allLeafChildren.reduce(
      (acc, childNode) => acc + (childNode?.data?.[fieldName] || 0), // Summing up values of fieldName
      0,
    );
    return formatCurrencyWithoutCents(totalAmount); // Format the summed amount
  }

  // For normal rows, format the specific field value
  return formatCurrencyWithoutCents(params.data[fieldName]);
}

const cellTextAligment = { textAlign: 'right' };
export const columnDefsKeys = [
  {
    headerName: 'Account Segment',
    field: 'account_segment',
    suppressMovable: true,
    // cellRenderer: 'agGroupCellRenderer',
    cellRendererSelector: params => {
      if (params.value === SKELETON_LOADER_CONST.LOADING) {
        return { component: () => <Skeleton /> };
      }
      return { component: 'agGroupCellRenderer' };
    },
    valueFormatter: params => {
      if (params.value && params.value !== 'Inbound') {
        return `${params.value} (Headquartered)`;
      }
      return params.value;
    },
    cellStyle: { fontWeight: 600 },
    minWidth: 520,
    hide: window.innerWidth < 600,
    headerComponent: TableHeader,
    headerComponentParams: {
      enableSorting: true,
      enableMenu: true,
      enableFilter: true, // Enable filter menu for this column
    },
    tooltipField: 'account_segment',
  },
  {
    headerName: 'Assurance',
    field: 'assurance',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_ACR,
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: params => currencyCellRenderer(params, 'assurance'),
    cellStyle: cellTextAligment,
    tooltipValueGetter: params => {
      const assuranceValue = params?.data?.assurance || 0;
      const formattedAssuranceValue = formatCurrencyWithoutCents(assuranceValue);
      return `${formattedAssuranceValue}`;
    },
  },
  {
    headerName: 'Consulting',
    field: 'consulting',
    sortable: true,
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellRenderer: params => currencyCellRenderer(params, 'consulting'),
    cellStyle: cellTextAligment,
    headerComponent: TableHeader,
    headerComponentParams: {
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
    },
    tooltipValueGetter: params => {
      const consultingValue = params?.data?.consulting || 0;
      const formattedConsultingValue = formatCurrencyWithoutCents(consultingValue);
      return `${formattedConsultingValue}`;
    },
  },
  {
    headerName: 'SAT',
    field: 'sat',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    headerComponent: TableHeader,
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_SAT,
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: params => currencyCellRenderer(params, 'sat'),
    cellStyle: cellTextAligment,
    tooltipValueGetter: params => {
      const satValue = params?.data?.sat || 0;
      const formattedSatValue = formatCurrencyWithoutCents(satValue);
      return `${formattedSatValue}`;
    },
  },

  {
    headerName: 'Tax',
    field: 'tax',
    sortable: true,
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    headerComponent: TableHeader,
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_TAX,
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: params => currencyCellRenderer(params, 'tax'),
    cellStyle: cellTextAligment,
    tooltipValueGetter: params => {
      const taxValue = params?.data?.tax || 0;
      const formattedTaxValue = formatCurrencyWithoutCents(taxValue);
      return `${formattedTaxValue}`;
    },
  },
  {
    headerName: 'ITTS',
    field: 'itts',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellRenderer: params => currencyCellRenderer(params, 'itts'),
    cellStyle: cellTextAligment,
    headerComponent: TableHeader,
    headerComponentParams: {
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
    },
    tooltipValueGetter: params => {
      const ittsValue = params?.data?.itts || 0;
      const formattedIttsValue = formatCurrencyWithoutCents(ittsValue);
      return `${formattedIttsValue}`;
    },
  },
  {
    headerName: 'EMEIA ACR',
    field: 'emiaacr',
    sortable: true,
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellStyle: cellTextAligment,
    cellRenderer: params => currencyCellRenderer(params, 'emiaacr'),
    headerComponent: TableHeader,
    headerComponentParams: {
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
    },
    tooltipValueGetter: params => {
      const emiaacrValue = params?.data?.emiaacr || 0;
      const formattedEmiaacrValue = formatCurrencyWithoutCents(emiaacrValue);
      return `${formattedEmiaacrValue}`;
    },
  },
  {
    headerName: 'Total',
    field: 'total',
    sortable: true,
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellStyle: cellTextAligment,
    cellRenderer: params => currencyCellRenderer(params, 'total'),
    headerComponent: TableHeader,
    headerComponentParams: {
      enableSorting: true,
      enableMenu: false,
      enableFilter: false,
    },
    tooltipValueGetter: params => {
      const totalValue = params?.data?.total || 0;
      const formattedTotalValue = formatCurrencyWithoutCents(totalValue);
      return `${formattedTotalValue}`;
    },
  },
];
